import React from 'react';
import { MapPin, StickyNote, ChevronDown } from 'lucide-react';
import { formatDuration } from '../utils/formatDuration';
import StatusSelect from './StatusSelect';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getOrdinal = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatGroupDate = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dateOnly = new Date(d);
  dateOnly.setHours(0, 0, 0, 0);

  const day = d.getDate();
  const weekday = WEEKDAYS[d.getDay()];
  const month = MONTHS[d.getMonth()];

  const year = d.getFullYear();
  if (dateOnly.getTime() === today.getTime()) return `Today — ${day}${getOrdinal(day)} ${month} ${year}`;
  if (dateOnly.getTime() === tomorrow.getTime()) return `Tomorrow — ${day}${getOrdinal(day)} ${month} ${year}`;
  return `${weekday}, ${day}${getOrdinal(day)} ${month} ${year}`;
};

const getDateKey = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const formatAppointmentType = (type) => {
  if (!type) return '';
  return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};


const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Reschedule...' },
];

/**
 * Timeline-style appointment card list with date grouping.
 *
 * Props:
 *  - appointments: array of appointment objects
 *  - onStatusChange(appointment, newStatus): status change handler
 *  - onClick(appointment): card click handler
 *  - showDateGroups: whether to group by date (default true)
 *  - showJobTitle: show job title (for appointment list page, default true)
 *  - showCustomerName: show customer name (default true)
 *  - renderActions: optional (appointment) => JSX for extra action buttons
 *  - renderDocs: optional (appointment) => JSX for linked documents
 *  - renderNotes: optional (appointment) => JSX for notes
 *  - maxItems: limit visible items (shows "See X more" button)
 *  - className: wrapper className
 */
const AppointmentTimeline = ({
  appointments,
  onStatusChange,
  onClick,
  showDateGroups = true,
  showJobTitle = true,
  showCustomerName = true,
  renderActions,
  renderDocs,
  renderNotes,
  maxItems,
  className = '',
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!appointments || appointments.length === 0) return null;

  // Group appointments by date
  const grouped = {};
  appointments.forEach(apt => {
    const key = getDateKey(apt.scheduled_date);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(apt);
  });

  const dateKeys = Object.keys(grouped).sort();
  const allSorted = dateKeys.flatMap(key => grouped[key]);

  const visibleItems = maxItems && !expanded ? allSorted.slice(0, maxItems) : allSorted;
  const hiddenCount = maxItems ? allSorted.length - maxItems : 0;

  // Build visible groups
  const visibleGrouped = {};
  visibleItems.forEach(apt => {
    const key = getDateKey(apt.scheduled_date);
    if (!visibleGrouped[key]) visibleGrouped[key] = [];
    visibleGrouped[key].push(apt);
  });
  const visibleDateKeys = Object.keys(visibleGrouped).sort();

  const renderCard = (apt) => {
    const isOverdue = new Date(apt.scheduled_date) < new Date() && !['completed', 'cancelled'].includes(apt.status);
    const techName = apt.assigned_technician_name || apt.technician_name;
    const title = showJobTitle ? (apt.job_title || 'Appointment') : null;
    const address = [apt.treatment_address_line1, apt.treatment_address_postcode].filter(Boolean).join(', ');
    return (
      <div
        key={apt.id}
        className={`rounded-xl shadow-sm hover:shadow-md border border-pest-blue/20 bg-pest-blue transition-all cursor-pointer ${
          apt.status === 'cancelled' ? 'opacity-60 hover:opacity-80' : ''
        }`}
        onClick={() => onClick && onClick(apt)}
      >
        <div className="flex">
          {/* Left: Time column */}
          <div className="flex-shrink-0 w-20 sm:w-24 flex flex-col items-center justify-center py-3 px-2 rounded-l-xl">
            <span className="text-xl sm:text-2xl font-bold leading-none text-white">
              {formatTime(apt.scheduled_date)}
            </span>
            {apt.estimated_duration && (
              <span className="text-xs text-white/70 mt-0.5 text-center">
                {formatDuration(apt.estimated_duration)}
              </span>
            )}
          </div>

          {/* Right: Details */}
          <div className="flex-1 min-w-0 p-2.5 sm:p-3 bg-white rounded-r-xl">
            {/* Top row: title/customer left, visit counter right */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                {apt.appointment_type && (
                  <div className="text-sm sm:text-base font-bold text-gray-900 truncate">
                    {formatAppointmentType(apt.appointment_type)}
                  </div>
                )}
                {title && (
                  <div className={`font-semibold text-gray-700 truncate ${apt.appointment_type ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'}`}>{title}</div>
                )}
                {showCustomerName && apt.customer_name && (
                  <div className="text-xs sm:text-sm text-gray-600 truncate">
                    {apt.customer_name}
                  </div>
                )}
              </div>
              {apt.visit_number && apt.total_visits && (
                <div className="flex-shrink-0 text-right">
                  <span className={`text-base sm:text-lg font-bold ${
                    apt.visit_number === apt.total_visits ? 'text-green-600' : 'text-gray-700'
                  }`}>
                    {apt.visit_number}/{apt.total_visits}
                  </span>
                  {apt.visit_number === apt.total_visits && (
                    <div className="text-xs font-medium text-green-600">Final</div>
                  )}
                </div>
              )}
            </div>

            {/* Info row: address, type */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              {address && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <MapPin size={11} className="flex-shrink-0 text-gray-400" />
                  <span className="truncate max-w-[180px] sm:max-w-none">{address}</span>
                </div>
              )}
            </div>

            {/* Technician */}
            {techName && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <div className="w-5 h-5 rounded-full bg-pest-blue text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {getInitials(techName)}
                </div>
                <span className="text-xs text-gray-600 truncate">{techName}</span>
              </div>
            )}

            {/* Notes */}
            {apt.notes && (
              <div className="flex items-start gap-1.5 mt-1.5">
                <StickyNote size={11} className="flex-shrink-0 text-gray-400 mt-0.5" />
                <p className="text-xs text-gray-500 line-clamp-2">{apt.notes}</p>
              </div>
            )}

            {/* Extra notes from parent (JobDetails ExpandableNote) */}
            {renderNotes && renderNotes(apt)}

            {/* Linked documents */}
            {renderDocs && renderDocs(apt)}

            {/* Bottom row: actions left, status right */}
            <div className="flex items-center justify-between mt-2">
              {/* Action buttons */}
              {renderActions ? (
                <div onClick={(e) => e.stopPropagation()}>
                  {renderActions(apt)}
                </div>
              ) : <div />}

              {/* Status - bottom right */}
              <div className="flex-shrink-0 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                {isOverdue && (
                  <div className="flex flex-col items-center mb-0.5">
                    <span className="text-xs font-semibold text-amber-600">Needs update</span>
                    <ChevronDown size={12} className="text-amber-500 animate-bounce" />
                  </div>
                )}
                <StatusSelect
                  value={apt.status || 'scheduled'}
                  onChange={(val) => onStatusChange && onStatusChange(apt, val)}
                  options={STATUS_OPTIONS}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!showDateGroups) {
    return (
      <div className={`space-y-2 ${className}`}>
        {visibleItems.map(renderCard)}
        {hiddenCount > 0 && !expanded && (
          <button
            onClick={() => setExpanded(true)}
            className="w-full text-center py-2 text-sm text-pest-blue hover:text-pest-teal transition-colors"
          >
            See {hiddenCount} more
          </button>
        )}
        {expanded && hiddenCount > 0 && (
          <button
            onClick={() => setExpanded(false)}
            className="w-full text-center py-2 text-sm text-pest-blue hover:text-pest-teal transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {visibleDateKeys.map(dateKey => (
        <div key={dateKey}>
          {/* Date group header */}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-pest-blue flex-shrink-0" />
            <h3 className="text-sm font-semibold text-gray-700">
              {formatGroupDate(visibleGrouped[dateKey][0].scheduled_date)}
            </h3>
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">{visibleGrouped[dateKey].length}</span>
          </div>

          {/* Cards for this date */}
          <div className="space-y-2">
            {visibleGrouped[dateKey].map(renderCard)}
          </div>
        </div>
      ))}

      {hiddenCount > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full text-center py-2 text-sm text-pest-blue hover:text-pest-teal transition-colors"
        >
          See {hiddenCount} more
        </button>
      )}
      {expanded && hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(false)}
          className="w-full text-center py-2 text-sm text-pest-blue hover:text-pest-teal transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  );
};

export default AppointmentTimeline;
